// src/lib/ws.svelte.ts
import { apiBase, isLocalApiBase } from './api.ts';
import { applyEvent } from './stores/agents.svelte.ts';
import { applyPmEvent } from './stores/pm.svelte.ts';
import type { AppEvent } from './types.ts';

const PM_EVENTS = new Set([
  'workspace_created',
  'project_created',
  'board_created',
  'column_created',
  'card_created',
  'card_updated',
  'card_moved',
  'card_deleted',
  'label_created',
  'card_label_added',
  'card_label_removed',
  'comment_added',
  'comment_deleted',
  // Chat
  'channel_created',
  'channel_deleted',
  'message_posted',
  'message_deleted',
]);

function deriveWsUrl(): string {
  const base = apiBase().replace(/^http/, 'ws');
  if (isLocalApiBase()) {
    return `${base}/ws`;
  }
  return `${base}/api/v1/realtime/default`;
}

const WS_URL = deriveWsUrl();

let socket: WebSocket | null = null;
let currentSocketClose: (() => void) | null = null;

export const ws = $state({
  connected: false,
  lastEventType: null as string | null,
});

type EventListener = (event: AppEvent) => void;
const listeners: EventListener[] = [];

export function onEvent(fn: EventListener): () => void {
  listeners.push(fn);
  return () => {
    const idx = listeners.indexOf(fn);
    if (idx !== -1) listeners.splice(idx, 1);
  };
}

export function connect(): void {
  if (socket?.readyState === WebSocket.OPEN) return;

  const newSocket = new WebSocket(WS_URL);
  let socketClosing = false;
  socket = newSocket;

  newSocket.addEventListener('open', () => {
    ws.connected = true;
  });

  newSocket.addEventListener('close', () => {
    ws.connected = false;
    if (socket === newSocket) socket = null;
    if (!socketClosing) setTimeout(connect, 3000);
  });

  newSocket.addEventListener('message', (event) => {
    if (typeof event.data !== 'string') return;
    try {
      const parsed = JSON.parse(event.data) as AppEvent;
      ws.lastEventType = parsed.event_type;
      if (PM_EVENTS.has(parsed.event_type)) {
        applyPmEvent(parsed);
      } else {
        applyEvent(parsed);
      }
      for (const fn of listeners) fn(parsed);
    } catch {
      // non-JSON message — ignore
    }
  });

  newSocket.addEventListener('error', () => {
    // close fires after error; reconnect handled there
  });

  // Store the per-socket closing setter so disconnect() can use it
  currentSocketClose = () => { socketClosing = true; newSocket.close(); };
}

export function send(data: string): void {
  if (socket?.readyState === WebSocket.OPEN) socket.send(data);
}

export function disconnect(): void {
  if (currentSocketClose) {
    currentSocketClose();
    currentSocketClose = null;
  } else {
    socket?.close();
  }
}
